import { INTEGRATION_LOGO_MAP } from '@polyai/ui/constants/integrations.constants';

import ENDPOINTS from 'api/endpoints';
import { niceMessagingHandoffFormConfig } from './forms/NiceMessagingHandoff';
import { OpenTableFormConfig } from './forms/OpenTable';
import { dialpadFormConfig } from './forms/telephony/Dialpad';
import { fiveNineFormConfig } from './forms/telephony/FiveNine';
import {
  twilioFormConfig,
  updateTwilioFormConfig,
} from './forms/telephony/TwilioForm';
import { zendeskMessagingHandoffFormConfig } from './forms/ZendeskMessagingHandoff';
import type { Integration } from './types';

export type {
  Integration,
  IntegrationCategory,
  TelephonyIntegration,
} from './types';

const INTEGRATIONS: {
  [id in keyof typeof INTEGRATION_LOGO_MAP]: Integration;
} = {
  // Telephony
  five9: {
    id: 'five9',
    name: 'Five9',
    category: 'telephony',
    description:
      "Integrate with Five9's cloud contact center platform to deliver smarter, AI-powered customer engagement.",
    formConfig: fiveNineFormConfig,
    noEdit: true,
    disconnectEndpoint: `${ENDPOINTS.TELEPHONY_GATEWAY_PROXY}/five9`,
    additionalConfig: {
      noConfigurableHandoffMethod: true,
      defaultHandoffMethod: 'bye',
    },
    featureFlag: 'five9_integration',
  },
  nice: {
    id: 'nice',
    name: 'NICE',
    category: 'telephony',
    description:
      "Connect with NICE's CXone platform for advanced contact center operations and seamless agent workflows.",
    additionalConfig: {},
  },
  twilio: {
    id: 'twilio',
    name: 'Twilio',
    category: 'telephony',
    description:
      "Enable communication with Twilio's programmable voice platform for scalable telephony and customer interactions.",
    disconnectEndpoint: `${ENDPOINTS.TELEPHONY_GATEWAY_PROXY}/twilio`,
    noEdit: true,
    additionalConfig: {
      noConfigurableHandoffMethod: true,
      defaultHandoffMethod: 'refer',
    },
    formConfig: twilioFormConfig,
    updateFormConfig: updateTwilioFormConfig,
  },
  'amazon-connect': {
    id: 'amazon-connect',
    name: 'Amazon Connect',
    category: 'telephony',
    description:
      "Integrate with Amazon's cloud-based contact center service for seamless customer interactions.",
    additionalConfig: {},
  },
  genesys: {
    id: 'genesys',
    name: 'Genesys',
    category: 'telephony',
    description:
      'Connect with Genesys Cloud CX for unified customer experience and intelligent call routing.',
    additionalConfig: {},
  },
  'zendesk-talk': {
    id: 'zendesk-talk',
    name: 'Zendesk Talk',
    category: 'telephony',
    description:
      'Integrate with Zendesk Talk to manage voice support directly within your Zendesk environment.',
    disconnectEndpoint: `${ENDPOINTS.TELEPHONY_GATEWAY_PROXY}/zendesk`,
    noEdit: true,
    additionalConfig: {
      noConfigurableHandoffMethod: true,
      noConfigurableRoute: true,
      defaultHandoffMethod: 'invite',
    },
  },
  zoom: {
    id: 'zoom',
    name: 'Zoom',
    category: 'telephony',
    description:
      'Integrate with Zoom to manage your meetings directly within your Zoom environment.',
    isParagonIntegration: true,
    featureFlag: 'paragonZoom',
    additionalConfig: {},
  },
  jira: {
    id: 'jira',
    name: 'Jira',
    category: 'projectManagement',
    description: 'Integrate with Jira to manage your project tasks and issues.',
    isParagonIntegration: true,
    featureFlag: 'paragonJira',
  },
  googleCalendar: {
    id: 'googleCalendar',
    name: 'Google Calendar',
    category: 'projectManagement',
    description:
      'Integrate with Google Calendar to manage events, meetings, and schedules.',
    isParagonIntegration: true,
    featureFlag: 'googleCalendarIntegrationsPage',
  },
  dialpad: {
    id: 'dialpad',
    name: 'Dialpad',
    category: 'telephony',
    description:
      "Connect with Dialpad's cloud contact center platform for advanced call management.",
    disconnectEndpoint: `${ENDPOINTS.TELEPHONY_GATEWAY_PROXY}/dialpad`,
    formConfig: dialpadFormConfig,
    noEdit: true,
    additionalConfig: {
      noConfigurableHandoffMethod: true,
      defaultHandoffMethod: 'refer',
    },
    featureFlag: 'dialpad_integration',
  },
  // Chat
  'nice-messaging-handoff': {
    id: 'nice-messaging-handoff',
    name: 'NICE CXone Messaging',
    category: 'chat',
    description:
      'Hand off chat conversations to NICE CXone agents via the Chat SDK.',
    formConfig: niceMessagingHandoffFormConfig,
    featureFlag: 'messagingHandoffIntegrationsPage',
    noEdit: true,
  },
  'zendesk-messaging-handoff': {
    id: 'zendesk-messaging-handoff',
    name: 'Zendesk Messaging',
    category: 'chat',
    description:
      'Hand off chat conversations to Zendesk agents via Sunshine Conversations.',
    formConfig: zendeskMessagingHandoffFormConfig,
    featureFlag: 'messagingHandoffIntegrationsPage',
    noEdit: true,
  },
  whatsapp: {
    id: 'whatsapp',
    name: 'WhatsApp',
    category: 'chat',
    description:
      'Integrate WhatsApp messaging to provide customers with real-time, conversational support on a familiar channel.',
  },
  sms: {
    id: 'sms',
    name: 'SMS',
    category: 'chat',
    description:
      'Connect with SMS messaging to deliver timely alerts, updates, and two-way conversations with customers.',
  },
  'salesforce-chat': {
    id: 'salesforce-chat',
    name: 'Salesforce Chat',
    category: 'chat',
    description:
      'Integrate with Salesforce Chat to provide customer engagement directly from your CRM.',
  },
  // CRM
  'salesforce-crm': {
    id: 'salesforce-crm',
    name: 'Salesforce CRM',
    category: 'crm',
    description:
      'Connect with Salesforce CRM to unify customer data and streamline sales, service, and marketing operations.',
  },
  zendesk: {
    id: 'zendesk',
    name: 'Zendesk CRM',
    category: 'crm',
    isParagonIntegration: true,
    paragonProviderId: 'zendesk',
    featureFlag: 'paragon_zendesk',
    description:
      'Integrate with Zendesk CRM to centralize customer interactions and enhance support efficiency.',
  },
  microsoftDynamics: {
    id: 'microsoftDynamics',
    name: 'Microsoft Dynamics 365',
    paragonProviderId: 'microsoftDynamics',
    category: 'crm',
    isParagonIntegration: true,
    featureFlag: 'paragon_microsoft_dynamics',
    description:
      'Connect with Microsoft Dynamics 365 for intelligent business applications and customer relationship management.',
  },
  hubspot: {
    id: 'hubspot',
    name: 'HubSpot',
    category: 'crm',
    isParagonIntegration: true,
    featureFlag: 'paragon_hubspot',
    description:
      'Integrate with HubSpot CRM to manage contacts, track interactions, and grow customer relationships.',
  },
  // Hospitality - Restaurant
  opentable: {
    id: 'opentable',
    name: 'OpenTable',
    category: 'hospitality',
    formConfig: OpenTableFormConfig,
    description:
      'Connect with OpenTable to manage restaurant reservations and streamline guest booking experiences.',
    isTemplateEngineIntegration: true,
    featureFlag: 'opentable_integration_page',
  },
  cendyn: {
    id: 'cendyn',
    name: 'Cendyn',
    category: 'hospitality',
    description:
      "Connect with Cendyn's hospitality CRM and sales platform for enhanced guest management.",
  },
  hotsos: {
    id: 'hotsos',
    name: 'HotSOS',
    category: 'hospitality',
    description:
      'Integrate with HotSOS to manage hotel operations, service requests, and staff workflows.',
  },
  // Healthcare (mapped to Hospitality as closest match - may need separate type)
  modmed: {
    id: 'modmed',
    name: 'ModMed',
    category: 'healthcare',
    description:
      "Connect with ModMed's EHR and practice management system for specialty-specific healthcare workflows.",
  },
  epic: {
    id: 'epic',
    name: 'Epic',
    category: 'healthcare',
    description:
      "Integrate with Epic's electronic health records system for comprehensive patient data.",
  },
  athenaone: {
    id: 'athenaone',
    name: 'athenaOne',
    category: 'healthcare',
    description:
      'Connect with athenaOne to access cloud-based EHR, medical billing, and patient engagement tools.',
  },
  'cerner-oracle-health': {
    id: 'cerner-oracle-health',
    name: 'Cerner (Oracle Health)',
    category: 'healthcare',
    description:
      "Integrate with Cerner's healthcare information system for patient data access and care coordination.",
  },
  raintree: {
    id: 'raintree',
    name: 'Raintree',
    category: 'healthcare',
    description:
      "Connect with Raintree's therapy EMR platform for streamlined patient records and practice management.",
  },
  // Knowledge Base (mapped to CRM as closest match - may need separate type)
  'zendesk-knowledge-management': {
    id: 'zendesk-knowledge-management',
    name: 'Zendesk Knowledge Management',
    category: 'knowledgeBase',
    description:
      "Integrate with Zendesk's knowledge base to deliver self-service support and centralized documentation.",
  },
  'salesforce-knowledge-management': {
    id: 'salesforce-knowledge-management',
    name: 'Salesforce Knowledge Management',
    category: 'knowledgeBase',
    description:
      'Connect with Salesforce Knowledge to manage scalable and centralized documentation.',
  },
  gladly: {
    id: 'gladly',
    name: 'Gladly',
    category: 'knowledgeBase',
    description:
      "Integrate with Gladly's customer service platform for a centralized knowledge hub and agent support.",
  },
  'azure-ai-search': {
    id: 'azure-ai-search',
    name: 'Azure AI Search',
    category: 'knowledgeBase',
    description:
      'Connect with Azure AI Search to power intelligent, AI-driven knowledge retrieval across content.',
  },
  servicenow: {
    id: 'servicenow',
    name: 'ServiceNow',
    category: 'knowledgeBase',
    paragonProviderId: 'servicenow',
    description:
      'Integrate with ServiceNow to access knowledge articles and ticket management.',
    isParagonIntegration: true,
    featureFlag: 'paragonServiceNow',
  },
  slack: {
    id: 'slack',
    name: 'Slack',
    category: 'chat',
    description: 'Integrate with Slack to access & manage your workspace.',
    isParagonIntegration: true,
    featureFlag: 'paragon_slack',
  },
  notion: {
    id: 'notion',
    name: 'Notion',
    category: 'knowledgeBase',
    description:
      'Integrate with Notion to access and manage your workspace pages, databases, and documentation.',
    isParagonIntegration: true,
    featureFlag: 'paragon_notion',
  },
  googleDrive: {
    id: 'googleDrive',
    paragonProviderId: 'googledrive',
    name: 'Google Drive',
    category: 'knowledgeBase',
    description:
      'Connect with Google Drive to access and manage files, folders, and shared documents.',
    isParagonIntegration: true,
    featureFlag: 'paragon_google_drive',
  },
  googleSheets: {
    id: 'googleSheets',
    name: 'Google Sheets',
    category: 'knowledgeBase',
    paragonProviderId: 'googlesheets',
    description:
      'Integrate with Google Sheets to access and manage spreadsheet data and collaborate in real-time.',
    isParagonIntegration: true,
    featureFlag: 'paragon_google_sheets',
  },
  microsoftTeams: {
    id: 'microsoftTeams',
    paragonProviderId: 'microsoftTeams',
    name: 'Microsoft Teams',
    category: 'chat',
    description:
      'Integrate with Microsoft Teams to access and manage your channels, messages, and team collaboration.',
    isParagonIntegration: true,
    featureFlag: 'paragon_microsoft_teams',
  },
  freshdesk: {
    id: 'freshdesk',
    name: 'Freshdesk',
    description:
      'Integrate with Freshdesk to manage customer support tickets and streamline helpdesk operations.',
    category: 'crm',
    isParagonIntegration: true,
    featureFlag: 'paragon_freshdesk',
  },
  confluence: {
    id: 'confluence',
    name: 'Confluence',
    description:
      'Integrate with Confluence to access and manage team wikis, documentation, and collaborative workspaces.',
    category: 'knowledgeBase',
    isParagonIntegration: true,
    featureFlag: 'paragon_confluence',
  },
  sharePoint: {
    id: 'sharePoint',
    name: 'SharePoint',
    paragonProviderId: 'sharepoint',
    description:
      'Integrate with Microsoft SharePoint to access and manage team sites, documents, and collaborative workspaces.',
    category: 'crm',
    isParagonIntegration: true,
    featureFlag: 'paragon_sharepoint',
  },
  oneDrive: {
    id: 'oneDrive',
    paragonProviderId: 'onedrive',
    name: 'OneDrive',
    description:
      'Integrate with Microsoft OneDrive to access and manage files and folders in the cloud.',
    category: 'knowledgeBase',
    isParagonIntegration: true,
    featureFlag: 'paragon_one_drive',
  },
  microsoftOutlook: {
    id: 'microsoftOutlook',
    paragonProviderId: 'outlook',
    name: 'Microsoft Outlook',
    description:
      'Integrate with Microsoft Outlook to access and manage emails, calendars, and contacts.',
    category: 'chat',
    isParagonIntegration: true,
    featureFlag: 'paragon_microsoft_outlook',
  },
  'custom.tripleseat': {
    id: 'custom.tripleseat',
    name: 'Tripleseat',
    category: 'hospitality',
    description:
      "Connect with Tripleseat's restaurant reservation and guest management platform.",
    disconnectDescription:
      'If large party handling is configured with Tripleseat this will no longer work as expected. The default handoff number will be used.',
    isParagonIntegration: true,
    featureFlag: 'paragon_tripleseat',
    isTemplateEngineIntegration: true,
  },
};

export const INTEGRATION_LIST: Integration[] = (
  Object.keys(INTEGRATIONS) as (keyof typeof INTEGRATIONS)[]
).map(id => ({
  ...INTEGRATIONS[id],
  Logo: INTEGRATION_LOGO_MAP[id],
}));

const PARAGON_PROVIDER_ID_MAP: Record<string, string> = Object.fromEntries(
  Object.values(INTEGRATIONS)
    .filter(i => i.paragonProviderId)
    .map(i => [i.id, i.paragonProviderId as string]),
);

// TODO: this can be removed when we make sure we always store paragon ID format
// currently there is some differences,, e.g: "sharepoint" in paragon is "SharePoint" in our set-up
export const resolveParagonProviderId = (integrationId: string): string =>
  PARAGON_PROVIDER_ID_MAP[integrationId] ?? integrationId;
